import { Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";

// Public founder biography page. Reachable from the footer of every
// sibling site (SocialPulse, IFT, FoodPulse, TravelerPulse, etc.).
// Photographs are served locally from /assets/founder/ so the page
// never depends on an external host — a mirror of the same portraits
// Tom uses across his other properties.
const FOUNDER_PORTRAIT     = "/assets/founder/portrait.jpg";
const FOUNDER_FOODPULSE    = "/assets/founder/foodpulse.webp";
const FOUNDER_SPICENFLAVOR = "/assets/founder/spicenflavor.jpg";

// Curated timeline used by the sticky left nav. Each id must match a
// `<Section id="…">` (or a `<section id="…">`) further down the page.
const FOUNDER_TOC = [
  { id: "intro",      label: "Overview" },
  { id: "1955",       label: "1955 · Kerala roots" },
  { id: "1971",       label: "1971 · College & Thankamma" },
  { id: "1974",       label: "1974 · Chennai kitchens" },
  { id: "1977",       label: "1977 · Saudi Arabia" },
  { id: "1980",       label: "1980 · Marriage" },
  { id: "1985",       label: "1985 · PCs in Kerala" },
  { id: "1986",       label: "1986 · Move to the USA" },
  { id: "1987",       label: "1987–96 · Wall Street" },
  { id: "internet",   label: "The early Internet" },
  { id: "1996",       label: "1996 · Spice N Flavor" },
  { id: "kerala",     label: "2000 · Kerala.com" },
  { id: "2016",       label: "2016 · Restaurant" },
  { id: "2022",       label: "2022 · Portland & AI" },
  { id: "ecosystem",  label: "The ecosystem" },
  { id: "family",     label: "Family" },
  { id: "together",   label: "Tom & Thankamma" },
  { id: "principles", label: "Principles" },
  { id: "vision",     label: "A personal vision" },
];

// Scroll-spy — highlights the TOC entry whose section is currently
// closest to the top of the viewport.
const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);
  return active;
};

const FounderTOC = ({ active, onJump }) => (
  <nav
    aria-label="Timeline"
    data-testid="founder-toc"
    className="text-sm"
  >
    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
      Timeline
    </div>
    <ol className="space-y-0.5 border-l border-zinc-200">
      {FOUNDER_TOC.map((entry) => {
        const isActive = active === entry.id;
        return (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              data-testid={`founder-toc-${entry.id}`}
              data-active={isActive ? "true" : undefined}
              onClick={(e) => {
                e.preventDefault();
                onJump(entry.id);
              }}
              className={`block -ml-px pl-3 py-1.5 border-l-2 transition-colors leading-snug ${
                isActive
                  ? "border-[#c99a3f] text-zinc-900 font-700 bg-zinc-50"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 hover:border-zinc-400"
              }`}
            >
              {entry.label}
            </a>
          </li>
        );
      })}
    </ol>
  </nav>
);

const Section = ({ title, children, id }) => (
  <section id={id} data-testid={id ? `founder-section-${id}` : undefined} className="space-y-4">
    {title && (
      <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight text-zinc-900 border-b border-zinc-200 pb-2">
        {title}
      </h2>
    )}
    <div className="space-y-4 text-zinc-700 leading-relaxed text-base">
      {children}
    </div>
  </section>
);

const Pull = ({ children }) => (
  <blockquote className="border-l-4 border-[#F4C77B] bg-zinc-50 px-5 py-4 my-3">
    <p className="font-heading font-700 text-lg text-zinc-800 leading-snug">
      {children}
    </p>
  </blockquote>
);

export default function MeetTheFounder() {
  useEffect(() => {
    document.title = "Meet the Founder — Tom Vellaringattu";
  }, []);

  const tocIds = FOUNDER_TOC.map((e) => e.id);
  const activeId = useActiveSection(tocIds);
  const jumpTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Offset for the fat SocialPulse chrome (~96px) so headings land
    // just below the sticky header instead of hiding underneath it.
    const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top: y, behavior: "smooth" });
    // Update the URL hash without triggering another scroll.
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  }, []);

  return (
    <div data-testid="meet-the-founder-page" className="bg-white">
      {/* ============= HERO ============= */}
      <header className="border-b border-zinc-200 bg-gradient-to-br from-zinc-50 to-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-[280px,1fr] gap-8 items-start">
          <img
            data-testid="founder-hero-photo"
            src={FOUNDER_PORTRAIT}
            alt="Tom (Thomas George) Vellaringattu"
            className="w-56 md:w-full mx-auto border border-zinc-300 shadow-lg"
            loading="eager"
          />
          <div className="space-y-3">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Meet the Founder
            </div>
            <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-zinc-900 leading-tight">
              Tom (Thomas George) Vellaringattu
            </h1>
            <p className="text-lg sm:text-xl text-zinc-700 font-heading">
              From a Village in Kerala to Food, Computers, the Internet and Artificial Intelligence
            </p>
            <p className="text-base italic text-zinc-600">
              A Life of Imagination, Reinvention, Family and Building What Comes Next
            </p>
          </div>
        </div>
      </header>

      {/* ============= BODY (two-column on desktop) ============= */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
        {/* Compact horizontal TOC for mobile */}
        <nav
          aria-label="Timeline (mobile)"
          data-testid="founder-toc-mobile"
          className="lg:hidden -mx-5 mb-6 overflow-x-auto scrollbar-thin"
        >
          <ol className="flex gap-2 px-5 whitespace-nowrap text-xs">
            {FOUNDER_TOC.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  onClick={(e) => { e.preventDefault(); jumpTo(entry.id); }}
                  data-testid={`founder-toc-m-${entry.id}`}
                  data-active={activeId === entry.id ? "true" : undefined}
                  className={`inline-block px-3 py-1.5 border rounded-full transition-colors ${
                    activeId === entry.id
                      ? "border-[#c99a3f] bg-[#F4C77B]/20 text-zinc-900 font-700"
                      : "border-zinc-300 text-zinc-600 hover:text-zinc-900"
                  }`}
                >
                  {entry.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="lg:grid lg:grid-cols-[220px,1fr] lg:gap-12">
          {/* Sticky left timeline (desktop only) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
              <FounderTOC active={activeId} onJump={jumpTo} />
            </div>
          </aside>

          {/* Main content column */}
          <div className="space-y-12 min-w-0">

        <Section id="intro">
          <p>
            Tom — Thomas George Vellaringattu — has spent his life moving through worlds that
            might, at first, seem unrelated: village life in Kerala, hospitality, professional
            cooking, Saudi Arabian construction camps, mining-company administration, personal
            computers, Wall Street technology, spice manufacturing, the early Internet,
            Malayalam computing, website publishing, restaurant entrepreneurship, food-waste
            reduction and, today, artificial intelligence.
          </p>
          <p>Yet there is a thread connecting almost every chapter.</p>
          <p>
            Tom has always been fascinated by possibilities — learning something unfamiliar,
            recognizing the potential of an emerging technology, connecting ideas that
            normally remain separate, and asking how those ideas might be transformed into
            something useful.
          </p>
          <p>His philosophy is inspired by Albert Einstein's famous observation:</p>
          <Pull>
            "Imagination is more important than knowledge. Knowledge is limited. Imagination
            encircles the world."
          </Pull>
          <p>
            For Tom, knowledge provides the foundation, but imagination determines where you
            might go with it.
          </p>
          <p>
            His journey is therefore less the story of a single career than one of continual
            learning, reinvention, experimentation and creation.
          </p>
        </Section>

        <Section title="1955 — The Beginning in Elivaly, Kerala" id="1955">
          <p>
            Thomas George Vellaringattu was born in 1955 at the Vellaringattu family home in
            Elivaly, Kadanad, Kerala, India.
          </p>
          <p>
            He was born the fourth of eight children to the late V. T. George Vellaringattu
            and Annamma Vellaringattu, née Kurisummoottil of Ponkunnam.
          </p>
          <p>
            Tom grew up with five sisters and two brothers. His sisters — Elcy, Mercy, Rani,
            Ancy and Lilly — live in Kerala. His brother James George Vellaringattu lives in
            the United States, while his brother Siby George Vellaringattu lives in Kerala.
          </p>
          <p>
            Those sibling relationships would eventually extend beyond family life into
            entrepreneurship. Decades later, James, Siby and Tom would become involved in
            some of Kerala's early personal-computer initiatives, while Tom and Siby would
            continue collaborating on Internet properties devoted to Kerala.
          </p>
          <p>
            Growing up as one of eight children also gave Tom an early appreciation of
            family, relationships and shared history — themes that would surface much later
            in projects devoted to preserving family histories, school friendships,
            photographs and memories.
          </p>
        </Section>

        <Section title="1960–1971 — School Years in Kadanad" id="school">
          <p>Tom's early education remained close to his family home.</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>1960–1964 — St. George's Lower Primary School, Elivaly</li>
            <li>1964–1968 — St. Augustine's Upper Primary School, Kadanad</li>
            <li>1968–1971 — St. Sebastian's High School, Kadanad</li>
          </ul>
          <p>
            These were years far removed from the technological world he would eventually
            enter. Personal computers had not entered homes, the Internet was decades away
            from public adoption, and artificial intelligence largely belonged to
            universities and research laboratories.
          </p>
          <p>Tom's life would eventually span all of those technological revolutions.</p>
        </Section>

        <Section title="1971–1973 — St. George's College and a Life-Changing Meeting" id="1971">
          <p>
            In 1971, Tom entered St. George's College, Aruvithura, for his Pre-Degree
            studies. His subjects included physics, mathematics, chemistry and Hindi.
          </p>
          <p>But something considerably more important than academics happened there.</p>
          <p>
            It was at St. George's College that Tom met{" "}
            <strong>Thankamma Thomas Thulvananickal</strong>, the young woman who would
            eventually become his wife and lifelong companion.
          </p>
          <p>
            Across decades of changing countries, professions, businesses and technologies,
            that meeting in 1971 would prove to be one of the most consequential moments of
            his life.
          </p>
        </Section>

        <Section title="1973–1974 — Entering Hospitality">
          <p>After Pre-Degree, Tom began professional training in hospitality.</p>
          <p>
            From 1973 to 1974, he studied Hotel Reception and Bookkeeping at the Food Craft
            Institute in Kalamassery, Kerala. The program introduced him to hotel operations,
            guest services, administration and bookkeeping.
          </p>
          <p>
            But his interests increasingly extended toward another side of hospitality —
            food and professional cooking.
          </p>
        </Section>

        <Section title="1974–1975 — Professional Cookery in Madras" id="1974">
          <p>
            Tom moved to Madras, now Chennai, where he studied Trade Cookery at the
            Institute of Catering Technology &amp; Applied Nutrition from 1974 to 1975. His
            training covered both Indian and Continental cookery.
          </p>
          <p>
            At the time, this was preparation for a hospitality career. Looking back,
            however, professional cookery became the foundation for several completely
            different chapters of his life.
          </p>
          <p>
            Food would repeatedly return — through institutional catering, packaged spice
            products, restaurant ownership, catering, meal delivery and eventually the
            development of FoodPulse.
          </p>
        </Section>

        <Section title="1974–1977 — Hotel Sudarsan International: Working While Learning">
          <p>
            While pursuing his education in Chennai, Tom also began gaining practical
            experience in the hotel industry.
          </p>
          <p>
            From 1974 to 1977, he worked at Hotel Sudarsan International in Egmore, Chennai,
            the property later known as Ambassador Pallava Hotel.
          </p>
          <p>His roles included:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Restaurant Helper</li>
            <li>Canteen Supervisor</li>
            <li>Back Office Administration Assistant</li>
          </ul>
          <p>
            Moving through these different responsibilities exposed him to several sides of
            hotel operations — from frontline restaurant work and food service to
            supervision and administration.
          </p>
          <p>
            He was not merely studying hospitality in classrooms. He was learning how a
            working hotel functioned every day.
          </p>
          <Pull>Learn something — and then learn much more by doing it.</Pull>
        </Section>

        <Section title="1975–1977 — Loyola College by Evening">
          <p>
            While working at Hotel Sudarsan International, Tom enrolled in the evening
            Bachelor of Commerce program at Loyola College, Nungambakkam, Chennai. His
            studies included accounting, commerce and economics. He completed approximately
            two years of the program.
          </p>
          <p>
            It was a demanding period — working during the day while pursuing college
            studies in the evening — but it gave him another layer of knowledge beyond
            cooking and hospitality.
          </p>
          <p>
            Business, accounting and commerce would later become useful when Tom began
            creating companies and products of his own.
          </p>
          <p>In 1977, another opportunity took him beyond India. He left Chennai for Saudi Arabia.</p>
        </Section>

        <Section title="1977–1979 — Construction Camp Catering in Saudi Arabia" id="1977">
          <p>
            Tom joined Taylor Plan Catering Company in Dammam, Saudi Arabia. From 1977 to
            1979, he worked in construction-camp catering, with responsibilities including
            cooking, dining hall management, store management, inventory and food
            operations.
          </p>
          <p>
            This was hospitality on an entirely different scale. Feeding workers in a
            construction environment required organization, purchasing, inventory control,
            planning, timing and efficient use of supplies.
          </p>
          <p>
            Decades later, many of those same practical questions — inventory, purchasing,
            consumption, surplus and waste — would reappear in his thinking about FoodPulse.
          </p>
        </Section>

        <Section title="1979–1986 — From Food to Administration">
          <p>
            In 1979, Tom made another significant career transition. He joined Watts,
            Griffis &amp; McOuat Limited, a Canadian geological and mining consulting
            company operating in Saudi Arabia.
          </p>
          <p>
            Based in Jeddah, he worked with the company until 1986, handling
            responsibilities involving office administration, travel arrangements, bookings
            and administrative coordination.
          </p>
          <p>
            The professionally trained cook and hospitality worker had now become an
            international office administrator.
          </p>
          <p>Another transformation was approaching.</p>
        </Section>

        <Section title="1980 — Marriage and a Partnership Across Continents" id="1980">
          <p>
            Tom and Thankamma married in 1980, nine years after first meeting as students in
            Kerala.
          </p>
          <p>
            Thankamma joined Tom in Saudi Arabia while developing a professional journey of
            her own.
          </p>
          <p>
            A Registered Nurse, she later traveled to the United Kingdom for higher studies,
            then returned to Saudi Arabia and continued working as a nurse there until 1986.
          </p>
          <p>
            Their lives would subsequently take them from Saudi Arabia to the United States,
            beginning another major chapter together.
          </p>
          <p>
            Through the career changes, business ventures, successes, disappointments and
            new experiments that followed, Thankamma would remain Tom's steadfast supporter
            and partner.
          </p>
        </Section>

        <Section title="1985 — Recognizing the Potential of Personal Computers" id="1985">
          <p>
            While still associated with Saudi Arabia, Tom was becoming fascinated by the
            emerging world of personal computers.
          </p>
          <p>
            In 1985, he helped his brothers Siby George Vellaringattu and James George
            Vellaringattu establish an early PC-based computer training company in Ernakulam
            — Cochin/Kochi, Kerala.
          </p>
          <p>
            Personal computers were still unfamiliar to much of the general public in India.
            The venture sought to introduce students and professionals to technology that
            Tom believed would eventually transform education, offices and business.
          </p>
          <Pull>
            Recognizing the possibilities of emerging technology before it becomes ordinary.
          </Pull>
          <p>
            Soon, computers would become much more than an interest. They would become his
            profession.
          </p>
        </Section>

        <Section title="1986–1987 — A New Life in the United States" id="1986">
          <p>In 1986, Tom and Thankamma left Saudi Arabia for the United States.</p>
          <p>
            For Tom, the move created an opportunity for one of the most dramatic
            professional reinventions of his life.
          </p>
          <p>For Thankamma, it began a long American nursing career.</p>
          <p>
            She worked in several New York hospitals, continuing a profession devoted to
            patient care while Tom began retraining for an entirely different career.
          </p>
        </Section>

        <Section title="1987 — From Hospitality to Computer Programming">
          <p>
            In 1987, Tom studied Mainframe Computer Programming at Grumman Data Systems in
            Woodbury, New York. His training included COBOL, CICS and JCL.
          </p>
          <p>
            He had moved from professional cooking and administration into computer
            programming. What might appear to be a radical departure actually suited Tom's
            natural approach to problems.
          </p>
          <p>
            Programming requires understanding a problem, breaking it into components,
            establishing rules and creating a system capable of solving it repeatedly. That
            systems-oriented way of thinking would influence almost everything Tom later
            built.
          </p>
        </Section>

        <Section title="1987–1996 — Wall Street Years" id="1987">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>1987–1989 — Doubleday Book &amp; Music Clubs</strong>, Garden City, NY — Assembler, COBOL, CICS, JCL. Large membership and transaction systems introduced him to enterprise-scale computing.</li>
            <li><strong>1989–1990 — First Chicago Trust Company of New York</strong> — stock-transfer and financial services, another highly data-intensive environment.</li>
            <li><strong>1990–1993 — Shearson Lehman Brothers</strong>, Senior Computer Programmer — deeper into the technology infrastructure supporting financial services and stock brokerage.</li>
            <li><strong>1993–1996 — Smith Barney</strong>, Systems Analyst, New York City — progressed from programming individual systems toward analyzing how larger technology and business processes worked together.</li>
          </ul>
          <p>
            The former restaurant helper and cook from Kerala was now developing and
            supporting computer systems on Wall Street.
          </p>
          <Pull>Don't simply build a feature. Understand the entire system around it.</Pull>
        </Section>

        <Section title="The Early Internet — Seeing Another Revolution Coming" id="internet">
          <p>
            While living and working in the United States, Tom encountered another emerging
            technology that captured his imagination: <strong>the Internet</strong>.
          </p>
          <p>
            He became convinced that the Internet would fundamentally change how people
            obtained information, communicated, conducted business, promoted tourism and
            remained connected across enormous distances.
          </p>
          <p>He was particularly interested in what this could mean for Kerala and India.</p>
          <p>
            For the first time, someone thousands of miles from Kerala could potentially
            discover its cities, villages, districts, businesses, hotels, culture, tourism
            and communities almost instantly.
          </p>
          <p>
            Tom began acquiring Internet domain names he believed could eventually become
            important gateways to such information. Among them were prominent domains such
            as <strong>Kerala.com</strong> and <strong>Indias.com</strong>, along with .com
            domains representing Kerala's districts and numerous vertical domains covering
            areas such as travel, hotels and other subjects.
          </p>
          <p>
            The objective was not simply domain ownership. Tom envisioned an interconnected
            network through which Kerala could present itself to the world.
          </p>
        </Section>

        {/* Second photo — Spice n Flavor era */}
        <section id="1996" data-testid="founder-section-1996" className="grid md:grid-cols-[1fr,240px] gap-8 items-start pt-2">
          <div className="space-y-4 text-zinc-700 leading-relaxed">
            <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight text-zinc-900 border-b border-zinc-200 pb-2">
              1996–2002 — India's Spice N Flavor
            </h2>
            <p>Even as technology became increasingly important in Tom's life, food returned.</p>
            <p>
              Through Ind-US Enterprises Inc. in Merrick, New York, Tom developed a food
              startup centered on Indian spice blends marketed under the India's Spice N
              Flavor brand.
            </p>
            <p>The venture brought together several earlier parts of his life:</p>
            <p className="font-mono text-sm bg-zinc-50 border border-zinc-200 px-3 py-2 rounded">
              Professional Cookery + Indian Cuisine + Commerce + Product Development +
              Packaging + Marketing + Entrepreneurship
            </p>
            <p>
              The products earned recognition, including a Best Packaging Award in India and
              Best Taste Awards for six products.
            </p>
            <Pull>
              Skills acquired earlier in life rarely disappear. They often return later in
              entirely different forms.
            </Pull>
          </div>
          <img
            data-testid="founder-photo-spicenflavor"
            src={FOUNDER_SPICENFLAVOR}
            alt="Tom Vellaringattu — Spice n Flavor, Flavorful India"
            className="w-48 md:w-full mx-auto border border-zinc-300 shadow-lg md:sticky md:top-24"
            loading="lazy"
          />
        </section>

        <Section title="Around 2000 — Kerala.com and Malayalam on the Early Web" id="kerala">
          <p>
            As Internet adoption expanded, Tom began actively publishing websites serving
            Kerala and the worldwide Malayali community. Kerala.com became an important part
            of this effort.
          </p>
          <p>But the early Internet presented a fundamental challenge for Malayalam speakers.</p>
          <p>
            Publishing Malayalam on computers was considerably more difficult than it is
            today. Modern Unicode support, smartphone Malayalam keyboards and easy online
            transliteration were not yet commonplace.
          </p>
          <p>
            Through Kerala.com, Tom worked with friends and information-technology
            professionals around the world to help make Malayalam more accessible online. An
            important part of this effort was <strong>Madhuri</strong>, Malayalam-language
            and transliteration software that helped users type and publish Malayalam using
            computers.
          </p>
          <Pull>
            Technology becomes truly useful when people can use it in their own language and
            within their own culture.
          </Pull>
        </Section>

        <Section title="2002–2016 — Worldviewer.com and the Expanding Web">
          <p>Tom's growing involvement with the Internet developed into another business chapter.</p>
          <p>
            Through Worldviewer.com Inc., initially operating from Merrick, New York and
            later from San Jose, California, he worked in website design, Internet
            publishing, search-engine optimization, online marketing and digital business
            development.
          </p>
          <p>
            This period spanned an extraordinary transformation of the Internet. Websites
            evolved from relatively simple desktop pages into search-driven businesses,
            ecommerce platforms, multimedia destinations, social networks and eventually
            mobile-first services.
          </p>
          <p>
            Tom continued experimenting with how the Internet could connect information,
            businesses, communities and people.
          </p>
        </Section>

        <Section title="Building a Network of Kerala Websites">
          <p>
            What began with early Internet domains developed into a substantial collection
            of specialized websites.
          </p>
          <p>
            Tom and his brother Siby George Vellaringattu continue to operate more than 50
            websites related to Kerala, continually redesigning, modernizing and updating
            them as technology and user expectations change.
          </p>
          <p className="font-mono text-sm bg-zinc-50 border border-zinc-200 px-4 py-3 rounded">
            Early Websites → Search Engines → Ecommerce → Social Media → Smartphones →
            Cloud Platforms → Artificial Intelligence
          </p>
          <p>The technology keeps changing. The underlying purpose remains remarkably similar:</p>
          <p className="font-heading font-700 text-zinc-900">
            Organize information. Connect people. Preserve culture. Make knowledge easier to
            discover.
          </p>
        </Section>

        <Section title="Thankamma's Nursing Career — New York to California">
          <p>
            Alongside Tom's changing careers and entrepreneurial ventures, Thankamma
            continued building a distinguished nursing career of her own.
          </p>
          <p>
            After working in several hospitals in New York, she continued nursing after the
            family's move to California.
          </p>
          <p>
            She ultimately spent <strong>16 years at Santa Clara Valley Medical Center</strong>,
            caring for patients and serving the community before retiring in December 2021.
          </p>
          <p>
            Throughout Tom's experiments — from computer programming and Internet ventures
            to food businesses, restaurant ownership and today's ambitious AI platforms —
            Thankamma has remained an ardent supporter through both successes and failures.
          </p>
          <p>Entrepreneurship inevitably brings both.</p>
          <p>
            Some ideas flourish. Others fail. Some disappear only to return years later in
            another form.
          </p>
          <p>Through all of those chapters, Thankamma has shared the journey.</p>
        </Section>

        {/* Third photo — FoodPulse / restaurant era */}
        <section id="2016" data-testid="founder-section-2016" className="grid md:grid-cols-[240px,1fr] gap-8 items-start pt-2">
          <img
            data-testid="founder-photo-foodpulse"
            src={FOUNDER_FOODPULSE}
            alt="Tom Vellaringattu — Food Pulse, Nourishing Communities"
            className="w-48 md:w-full mx-auto"
            loading="lazy"
          />
          <div className="space-y-4 text-zinc-700 leading-relaxed">
            <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight text-zinc-900 border-b border-zinc-200 pb-2">
              2016–2022 — Returning to the Kitchen: Spice N Flavor Restaurant
            </h2>
            <p>In 2016, Tom returned once again to his earliest professional field — food.</p>
            <p>
              He opened Spice N Flavor Restaurant in San Jose, California, serving as chef,
              owner, caterer and restaurant operator. The business provided restaurant
              dining, catering and meal delivery.
            </p>
            <p>
              After decades in computing and Internet businesses, Tom was once again
              standing inside a commercial kitchen. But this time he saw the restaurant
              through several different lenses simultaneously:
            </p>
            <p className="font-heading font-700 text-zinc-900">
              Chef + Programmer + Entrepreneur + Systems Analyst
            </p>
            <p>
              Restaurant ownership gave him firsthand experience with purchasing, inventory,
              labor, pricing, customer behavior, delivery, spoilage and waste.
            </p>
            <p>
              The restaurant also participated in the early development of the home-chef
              delivery concept associated with Shef.com, helping with its launch and serving
              more than <strong>11,000 meals</strong> over approximately four years.
            </p>
          </div>
        </section>

        <Section title="A Restaurant Leads to a Bigger Question">
          <p>Operating a restaurant changed the way Tom thought about food.</p>
          <p>A restaurant doesn't simply cook food. Food must be:</p>
          <p className="font-mono text-sm bg-zinc-50 border border-zinc-200 px-4 py-3 rounded">
            Purchased → Received → Stored → Monitored → Prepared → Priced → Sold → Delivered
          </p>
          <p>And sometimes:</p>
          <p className="font-mono text-sm bg-zinc-50 border border-zinc-200 px-4 py-3 rounded">
            Discounted → Donated → or Discarded
          </p>
          <p>
            The same pattern occurs throughout the food system. Farmers may have crops they
            cannot sell. Supermarkets have products approaching expiration. Bakeries have
            unsold bread. Restaurants have prepared food remaining at closing. Families
            forget food in refrigerators.
          </p>
          <p>At the same time, other people struggle to obtain sufficient food.</p>
          <Pull>
            What if technology could connect all of these points before usable food becomes
            waste?
          </Pull>
          <p>That question became central to FoodPulse.net.</p>
        </Section>

        <Section title="2021 — Retirement Opens Another Family Chapter">
          <p>
            When Thankamma retired from nursing in December 2021, it opened another chapter
            for Tom and Thankamma.
          </p>
          <p>
            After a life that had taken them from Kerala to Saudi Arabia, the United
            Kingdom, New York and California, they could devote more time to something they
            both deeply enjoy:
          </p>
          <p className="font-heading font-700 text-xl text-zinc-900 text-center">
            Traveling the world together.
          </p>
          <p>
            For them, travel is not merely tourism. It is discovery, food, photography,
            culture, people, memories and shared experience.
          </p>
          <p>That personal love of travel would also influence Tom's vision for TravelerPulse.</p>
        </Section>

        <Section title="2022–Present — Portland and the Artificial Intelligence Revolution" id="2022">
          <p>
            Beginning around 2022, Tom entered another entrepreneurial chapter through
            Social Pulse Inc. in Portland, Oregon.
          </p>
          <p>This time, the enabling technology was artificial intelligence.</p>
          <p>
            For someone who began programming computers in the 1980s, AI represents an
            extraordinary evolution.
          </p>
          <p>
            Traditional programming required the programmer to explicitly describe much of
            what a computer should do. Modern AI tools increasingly allow the creator to
            begin differently:
          </p>
          <Pull>Here is the problem. Here is what I imagine. How can we build it?</Pull>
          <p>
            Tom now uses AI agents extensively to research, conceptualize, design and help
            build increasingly sophisticated websites, databases, social platforms,
            marketplaces and ecommerce systems.
          </p>
          <p>
            AI fits naturally with the philosophy that has followed him throughout his life:
          </p>
          <p className="font-heading font-700 text-lg text-zinc-900">
            Imagine first. Learn what is necessary. Then try to build it.
          </p>
        </Section>

        <Section title="Building an Ecosystem, Not Just Websites" id="ecosystem">
          <p>
            Tom's present work is not focused on creating a single website or company. He is
            developing a family of interconnected platforms addressing different aspects of
            everyday human experience.
          </p>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">TravelerPulse.pro™ — Explore, Plan, Share and Remember</div>
            <p className="text-sm text-zinc-700">
              A global travel discovery, planning and personal travel-management ecosystem.
              Helps travelers explore destinations, plan journeys, organize flights, trains
              and other transportation, research accommodations and food, manage travel
              information, store sensitive trip data privately, track visas and entry
              requirements, maintain Travel Diaries, create personal Memoirs, preserve
              photographs and experiences, participate in traveler discussions and share
              travel stories.
            </p>
            <p className="text-sm text-zinc-700">
              For Tom and Thankamma, who love traveling together, TravelerPulse is also
              deeply connected to their own experiences of planning, exploring and
              remembering journeys.
            </p>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 pt-1">
              Explore • Plan • Share • Travel
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">AlumniPulse.net — Reconnecting the People Who Shared Our Education</div>
            <p className="text-sm text-zinc-700">
              Connects educational institutions and the people whose lives passed through
              them: schools, colleges and universities worldwide; alumni communities; class
              and batch communities; teacher and staff profiles; find-a-lost-colleague
              tools; reunions, memories, historical photographs, institution histories,
              memorials, career networking, mentoring and community discussions.
            </p>
            <p className="text-sm italic text-zinc-700">
              Why should people who helped shape our lives become strangers simply because
              decades have passed?
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">FoodPulse.net — Food, Technology and Reducing Waste</div>
            <p className="text-sm text-zinc-700">
              Brings together perhaps more chapters of Tom's career than any other project:
              Cookery + Catering + Restaurant Operations + Inventory + Entrepreneurship +
              Programming + Databases + Internet Technology + Artificial Intelligence.
            </p>
            <p className="text-sm text-zinc-700">
              Connects farmers, fishermen, producers, groceries, supermarkets, bakeries,
              restaurants, wholesalers, distributors, consumers, food banks, charities and
              food-recovery organizations. Beyond "what food can we sell?" it asks: what can
              we save, discount, bundle, share, donate — and how can technology prevent
              usable food from becoming waste?
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">PhotoPulse.pro™ — Giving Photographs Another Life</div>
            <p className="text-sm text-zinc-700">
              People take enormous numbers of beautiful photographs — sunsets, landscapes,
              flowers, mountains, cities, beaches, birds and everyday scenes. Most are
              rarely seen again, buried inside phones, computers and cloud accounts.
              PhotoPulse gives them another life by organizing and discovering them through
              geography, AI image understanding, subjects, descriptions, categories, tags
              and photographer attribution — a crowdsourced photographic record of the
              world for future generations.
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">NaturePulse.net — Documenting a Changing Natural World</div>
            <p className="text-sm text-zinc-700">
              Seasons change. Flowers bloom. Birds migrate. Storms arrive. Landscapes
              evolve. NaturePulse combines observations, photographs, weather information
              and geographic documentation into a growing record of the natural world.
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">IndianFamilyTree.com — Connecting Generations</div>
            <p className="text-sm text-zinc-700">
              Preserving family histories and relationships — family trees, individual life
              stories, photographs, births and marriages, family milestones, obituaries,
              memorials and generational histories.
            </p>
            <p className="text-sm text-zinc-700">
              For someone who grew up as one of eight children in Kerala and whose family is
              now spread across India and the United States, the idea has a personal
              resonance. A family story that is never recorded can disappear within only a
              few generations. Technology can help prevent that.
            </p>
          </div>

          <div className="border border-zinc-300 bg-white p-5 space-y-2">
            <div className="font-heading font-700 text-lg text-zinc-900">SocialPulse.com — Building Social Capital</div>
            <p className="text-sm text-zinc-700">
              Explores how social technology might be used for more than entertainment,
              followers and popularity — recognizing volunteer activities, community
              participation, helpful actions, good deeds, knowledge sharing and local
              contributions. Centered on helping people build and document social capital:
              the positive contributions they make to other people and their communities.
            </p>
          </div>
        </Section>

        <Section title="Family — The Constant Through All the Changes" id="family">
          <p>
            Behind the careers, businesses, migrations, travels and technological
            experiments is Tom's family.
          </p>
          <p>
            Tom and Thankamma are the parents of two daughters, <strong>Tanya</strong> and{" "}
            <strong>Teresa</strong>.
          </p>
          <p>
            Their elder daughter, <strong>Tanya Thomas Coss</strong>, works as a media
            analyst with IBM Corporation in New York City. She lives in New York with her
            husband, Sebastian Coss, and their sons, William and Henry.
          </p>
          <p>
            Their younger daughter, <strong>Teresa</strong>, followed her mother into
            nursing. She is a Registered Nurse in the Neonatal Intensive Care Unit at Legacy
            Emanuel Hospital in Portland, Oregon, caring for vulnerable newborn patients
            while also living close to her parents.
          </p>
          <p>
            The family's story now stretches across several generations and continents —
            from Kerala to Saudi Arabia, the United Kingdom and the United States, and
            across careers in nursing, technology, media, healthcare, entrepreneurship and
            artificial intelligence.
          </p>
          <p>
            For Tom, family remains one of the constants behind everything else that has
            changed.
          </p>
        </Section>

        <Section title="Tom and Thankamma — Still Traveling Together" id="together">
          <p>
            The relationship that began when Tom and Thankamma met as young students in 1971
            has continued through more than half a century of friendship and shared
            experiences.
          </p>
          <p>They married in 1980.</p>
          <p>
            They have lived through migrations, careers, raising a family, entrepreneurship,
            successes, failures, retirement and constant technological change.
          </p>
          <p>Today, they continue doing something they both love:</p>
          <p className="font-heading font-700 text-xl text-zinc-900 text-center">
            Traveling and exploring the world together.
          </p>
          <p>
            In many ways, their shared love of travel gives additional personal meaning to
            Tom's work on TravelerPulse. Travel is not simply another technology project.
            It is part of their life.
          </p>
          <p>
            Through all of Tom's entrepreneurial experiments, Thankamma has remained his
            steadfast supporter — celebrating the successes, enduring the disappointments
            and encouraging the next idea.
          </p>
          <p>
            Some things in Tom's life have changed repeatedly. That partnership has not.
          </p>
        </Section>

        <Section title="From COBOL to AI Agents">
          <p>
            Few things illustrate the span of Tom's journey better than the technologies
            themselves.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="border border-zinc-300 bg-white p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">1980s</div>
              <div className="text-sm text-zinc-800 font-mono">COBOL • CICS • JCL • Assembler • Personal Computers</div>
            </div>
            <div className="border border-zinc-300 bg-white p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">1990s–2010s</div>
              <div className="text-sm text-zinc-800 font-mono">The Internet • Websites • Search • SEO • Ecommerce • Databases • Smartphones • Cloud</div>
            </div>
            <div className="border border-zinc-300 bg-white p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Today</div>
              <div className="text-sm text-zinc-800 font-mono">Artificial Intelligence • AI Agents • Automation • Intelligent Platforms</div>
            </div>
          </div>
          <p>
            But for Tom, the important question has never really been the technology itself.
            It is:
          </p>
          <Pull>What can this technology allow us to do that wasn't practical before?</Pull>
        </Section>

        <Section title="A Career of Connected Chapters">
          <p>On paper, Tom's career may appear to contain many unrelated lives:</p>
          <p className="font-mono text-sm bg-zinc-50 border border-zinc-200 px-4 py-3 rounded leading-relaxed">
            Student → Hotel Worker → Cook → Catering Professional → Administrator → PC
            Training Entrepreneur → Mainframe Programmer → Systems Analyst → Spice
            Entrepreneur → Internet Publisher → Web Developer → SEO Consultant → Chef &amp;
            Restaurant Owner → Food-Waste Innovator → AI Platform Builder
          </p>
          <p>But Tom sees them differently. Each experience added another layer.</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Cooking taught him food.</li>
            <li>Hotel work taught him service.</li>
            <li>Catering taught him operations and inventory.</li>
            <li>Administration taught him organization.</li>
            <li>Personal computers showed him the transformative power of emerging technology.</li>
            <li>Programming taught him logic and systems.</li>
            <li>Wall Street taught him scale and data.</li>
            <li>Spice manufacturing taught him products, packaging and branding.</li>
            <li>The Internet taught him global connectivity.</li>
            <li>Kerala.com demonstrated the importance of culture and language online.</li>
            <li>Restaurant ownership taught him the realities of inventory, customers, delivery and food waste.</li>
            <li>Travel reinforced his fascination with geography, culture and human experience.</li>
          </ul>
          <p>
            And now artificial intelligence allows him to combine many of those experiences
            into systems that would once have required large teams and substantial
            resources to build.
          </p>
        </Section>

        <Section title="Recognizing Technology Before It Becomes Ordinary">
          <p>There is another recurring pattern in Tom's journey.</p>
          <p>
            In 1985, when personal computers were still unfamiliar to much of India, he
            helped establish a PC-based training venture in Kerala.
          </p>
          <p>
            When the commercial Internet was still emerging, he recognized the value of
            Internet domains and began building Kerala-focused websites.
          </p>
          <p>
            When Malayalam was difficult to use online, he worked with friends and
            technology professionals to help make Malayalam publishing and transliteration
            more accessible.
          </p>
          <p>
            And today, he is exploring how AI agents can help individuals build
            sophisticated global platforms.
          </p>
          <p>Different technologies. The same question:</p>
          <Pull>What can this new technology make possible that was not possible before?</Pull>
        </Section>

        <Section title="Preserving What Otherwise Disappears">
          <p>
            Perhaps the strongest connection among Tom's current projects is a single word:{" "}
            <strong>Preservation.</strong>
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>FoodPulse</strong> — preserve usable food before it becomes waste.</li>
            <li><strong>PhotoPulse</strong> — preserve photographs before they disappear into forgotten digital archives.</li>
            <li><strong>AlumniPulse</strong> — preserve friendships, teachers, classmates and institutional memories.</li>
            <li><strong>IndianFamilyTree</strong> — preserve family histories across generations.</li>
            <li><strong>TravelerPulse</strong> — preserve journeys, diaries, memoirs and experiences.</li>
            <li><strong>NaturePulse</strong> — preserve observations of our changing natural world.</li>
          </ul>
          <p>Different subjects. Different technologies. One common philosophy:</p>
          <Pull>
            Things that matter should not disappear simply because nobody created a system
            to preserve them.
          </Pull>
        </Section>

        <Section title="Entrepreneurship as Continuous Experimentation">
          <p>Tom does not view entrepreneurship as a straight road.</p>
          <p>
            An idea may succeed. It may fail. It may change direction. It may disappear. Or
            it may return twenty years later in a completely different form.
          </p>
          <p>A discontinued experiment is not necessarily wasted effort. It becomes knowledge.</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Every problem reveals a constraint.</li>
            <li>Every constraint can inspire another solution.</li>
            <li>Every solution can reveal another possibility.</li>
          </ul>
          <p>Tom summarizes this thinking as:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-zinc-300 bg-white p-4 text-center font-mono text-sm font-700 text-zinc-900">
              Imagination + Knowledge + Persistence = Possibility
            </div>
            <div className="border border-zinc-300 bg-white p-4 text-center font-mono text-sm font-700 text-zinc-900">
              Possibility + Action = Creation
            </div>
          </div>
          <p>Instead of beginning with:</p>
          <p className="italic text-zinc-600">"Can this be done?"</p>
          <p>he prefers to begin with:</p>
          <p className="font-heading font-700 text-xl text-zinc-900">"What if it could be done?"</p>
          <p>Only then come the practical questions:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>What knowledge do we need?</li>
            <li>What technology is available?</li>
            <li>What has to be invented?</li>
            <li>Who could benefit?</li>
            <li>How can it be made simpler?</li>
            <li>Can different systems be connected?</li>
            <li>Can AI make something possible today that was prohibitively difficult only a few years ago?</li>
          </ul>
        </Section>

        <Section title="Tom's Personal Principles" id="principles">
          <ol className="space-y-3">
            {[
              ["Imagine First","Don't allow today's limitations to define tomorrow's possibilities."],
              ["Learn Continuously","Knowledge supplies the tools necessary to transform imagination into reality."],
              ["Connect the Unconnected","Some of the most interesting ideas emerge when unrelated concepts are brought together."],
              ["Build for People","Technology has value when it improves real human experiences."],
              ["Experiment","Ideas reveal their value only when someone is willing to try them."],
              ["Learn From Failure","An unsuccessful experiment can provide the knowledge necessary for the next one."],
              ["Recognize Change Early","Emerging technologies create opportunities for people willing to explore them."],
              ["Share Knowledge","Ideas become more powerful when others can use and improve upon them."],
              ["Preserve What Matters","People, memories, culture, photographs, food, nature, journeys and knowledge deserve to be documented."],
              ["Never Stop Imagining","There is always another possibility worth exploring."],
            ].map(([title, body], idx) => (
              <li key={title} className="border border-zinc-300 bg-white p-4 flex gap-4">
                <span className="font-mono font-700 text-zinc-500 shrink-0 w-6 text-right">{idx+1}.</span>
                <div>
                  <div className="font-heading font-700 text-base text-zinc-900">{title}</div>
                  <p className="text-sm text-zinc-700 mt-0.5">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section title="His Mission Today">
          <p>
            At this stage of his journey, Tom's objective is not simply to pursue another
            conventional career. It is to transform a lifetime of accumulated experience
            into useful ideas, platforms and communities.
          </p>
          <p>He seeks to:</p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Imagine beyond conventional boundaries</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Continue learning regardless of age or previous profession</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Apply artificial intelligence creatively</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Solve practical everyday problems</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Connect information currently trapped in separate silos</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Preserve memories and history</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Reduce unnecessary food waste</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Connect individuals, families and communities</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Encourage participation and contribution</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Make useful knowledge easier to discover</li>
            <li className="flex gap-2"><span className="text-zinc-400">•</span>Build systems that can continue evolving beyond their creator</li>
          </ul>
        </Section>

        <Section title="A Personal Vision" id="vision">
          <p>Tom describes the philosophy behind his work this way:</p>
          <Pull>
            "I want to spend my life imagining possibilities, learning what is necessary to
            make them real, and creating things that can help people, communities and the
            world. I don't want to be limited by what already exists. I want to ask what
            could exist. I believe knowledge gives us the foundation, but imagination gives
            us the courage to build beyond it."
          </Pull>
          <p>
            From Elivaly to Chennai, Saudi Arabia, New York, San Jose and Portland, the
            surroundings have changed.
          </p>
          <p>
            From hotel restaurants and construction-camp kitchens to Wall Street computer
            systems, the professions have changed.
          </p>
          <p>
            From personal computers to the Internet and artificial intelligence, the
            technologies have changed.
          </p>
          <p>
            From a young student in Kerala to a husband, father and grandfather, life itself
            has changed.
          </p>
          <p>But the curiosity behind it all has remained.</p>
          <p className="font-heading font-700 text-lg text-zinc-900">
            Imagine beyond what you know. Learn what you need. Build what you imagine.
          </p>
        </Section>

        {/* ============= FOOTER SIGNATURE ============= */}
        <div className="border-t border-zinc-200 pt-8 text-center space-y-3">
          <div className="font-heading font-900 text-xl text-zinc-900">
            Tom (Thomas George) Vellaringattu
          </div>
          <div className="text-sm text-zinc-600 italic">
            Entrepreneur • Chef • Programmer • Systems Thinker • Internet Publisher • AI Platform Builder
          </div>
          <div className="text-sm text-zinc-600 max-w-2xl mx-auto pt-2">
            Founder and developer of initiatives including TravelerPulse.pro™,
            AlumniPulse.net, FoodPulse.net, PhotoPulse.pro™, NaturePulse.net,
            IndianFamilyTree.com, SocialPulse.com, and a continuing network of
            Kerala-focused Internet properties developed and maintained with his brother
            Siby George Vellaringattu.
          </div>
          <div className="text-sm text-zinc-600 max-w-2xl mx-auto pt-2 italic">
            And alongside him through much of that extraordinary journey has been Thankamma
            Vellaringattu — nurse, wife, mother, traveling companion and steadfast supporter
            through the successes, failures and everything in between.
          </div>
          <div className="text-sm text-zinc-600 max-w-2xl mx-auto pt-2">
            Their story continues through their daughters Tanya and Teresa, their sons-in-law
            and grandchildren, and through a family whose roots remain connected to Kerala
            even as its branches have spread around the world.
          </div>
          <p className="font-heading font-700 text-base text-zinc-800 max-w-2xl mx-auto pt-4">
            A life may be measured in years, but its story is carried forward through the
            people, memories, experiences and ideas we leave behind.
          </p>
          <p className="text-sm text-zinc-500 italic pt-2">
            And the next chapter is still being imagined.
          </p>
          <div className="pt-6">
            <Link
              to="/"
              data-testid="founder-back-home"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-300 bg-white text-zinc-900 text-sm font-700 hover:bg-zinc-50 transition-colors"
            >
              ← Back home
            </Link>
          </div>
        </div>
          </div> {/* /main content column */}
        </div> {/* /two-column grid */}
      </div>
    </div>
  );
}
